import React from 'react';
import {TFunction} from 'react-i18next';
import {useUserContext} from 'authentication/UserContext';
import {getParagraphListBaseColumns} from '../ParagraphListBaseColumns';
import {EntityListColumn} from 'components';

const hiddenForAvk = new Set<string>([
	'p-vehicle-category',
	'p-tags',
	'p-drive-variants',
	'p-dateNewTypes',
	'p-dateNewRegistration',
	'p-phaseIn',
	'p-phaseOut',
	'p-requirements',
	'p-changeInfoType',
]);

const hiddenForVko = new Set<string>(['p-requirements', 'p-tags']);

const hiddenForReader = new Set<string>([
	'p-categories',
	'p-vehicle-category',
	'p-tags',
	'p-drive-variants',
	'p-dateNewTypes',
	'p-dateNewRegistration',
	'p-phaseIn',
	'p-phaseOut',
]);

const hiddenForBedarfskoordinator = new Set<string>([
	'p-categories',
	'p-vehicle-category',
	'p-drive-variants',
	'p-dateNewTypes',
	'p-dateNewRegistration',
	'p-phaseIn',
	'p-phaseOut',
]);

export type UseParagraphListColumnsParams = {
	t: TFunction;
	tt: TFunction;
	tEnum: TFunction;
	filterable?: boolean;
	lazyLoadedTooltips?: boolean;
	shouldShowFaq?: boolean;
	myKeywords?: any;
	isReqifz?: boolean;
};

export const useParagraphListColumns = ({
	t,
	tt,
	tEnum,
	filterable = true,
	lazyLoadedTooltips = false,
	shouldShowFaq,
	myKeywords,
	isReqifz = false,
}: UseParagraphListColumnsParams): EntityListColumn[] => {
	const {isAvk, isBav, isVex, isVko, isReader, isBedarfskoordinator} =
		useUserContext();
	const isAvkOrBav = isAvk || isBav;

	// Determine which columns to exclude early, based on role
	const roleExcludedColumns = React.useMemo((): Set<string> | undefined => {
		// VKO needs all columns (will mark as hidden later for side panel)
		if (isVko) return undefined;
		// For other roles: determine excluded columns early to avoid creating them
		if (isAvkOrBav) return hiddenForAvk;
		if (isReader) return hiddenForReader;
		if (isBedarfskoordinator) return hiddenForBedarfskoordinator;
		return undefined;
	}, [isAvkOrBav, isVex, isVko, isReader, isBedarfskoordinator]);

	const baseColumns = React.useMemo(
		() =>
			getParagraphListBaseColumns(t, tt, tEnum, {
				filterable,
				lazyLoadedTooltips,
				shouldShowFaq,
				myKeywords,
				isReqifz,
				excludedColumnKeys: roleExcludedColumns,
			}),
		[
			t,
			tt,
			tEnum,
			filterable,
			lazyLoadedTooltips,
			shouldShowFaq,
			myKeywords,
			isReqifz,
			roleExcludedColumns,
		],
	);

	return React.useMemo(() => {
		// For VKO: Mark columns as hidden but keep them in the list (show in side panel)
		if (isVko) {
			return baseColumns.map(col => {
				if (hiddenForVko.has(col.key ?? '')) {
					return {
						...col,
						hidden: true,
					};
				}
				return col;
			});
		}
		// For other roles: columns are already filtered by roleExcludedColumns
		return baseColumns;
	}, [baseColumns, isVko]);
};
